// Blocks the app when nothing pays for it (see SubscriptionPopupHost). It can't be dismissed, only
// left by choosing a plan, switching to another workspace or logging out.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ArrowRight, ChevronsUpDown, Lock, LogOut, ShieldCheck } from "lucide-react";

import Dialog from "../Account/billing/Dialog";
import { Avatar, WorkspaceMenuItems } from "../../components/WorkspaceMenu";
import api from "../chat/api";
import { formatMoney } from "../../services/rfpBillingApi";
import { clearSession } from "../../utils/session";
import { switchWorkspace } from "../../utils/workspace";

const DEFAULT_SEAT_PRICE_CENTS = 9900;

const readJSON = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch (_) {
    return fallback;
  }
};

const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;

const MENU_WIDTH = 240;
const MENU_MAX_HEIGHT = 288; // max-h-72
const MENU_ROW_HEIGHT = 56;
const GAP = 6;

// reason -> what the popup says. `plan` is the workspace's RFP plan (absent for "personal_trial").
const copyFor = (reason, plan) => {
  if (reason === "expired") {
    const ended = shortDate(plan?.current_period_end);
    return { title: "Your plan has ended", badge: ended ? `Plan · ended ${ended}` : null };
  }
  if (reason === "none") return { title: "Choose a plan to continue", badge: null };
  const ended = shortDate(plan?.trial_ends_at);
  return { title: "Your trial has ended", badge: ended ? `Free trial · ended ${ended}` : "Free trial ended" };
};

const SubscriptionPopup = ({ reason = "trial_expired", plan = null }) => {
  const navigate = useNavigate();
  const currentId = String(Cookies.get("selected_org_id") || "");
  const [workspaces, setWorkspaces] = useState(() => readJSON("cachedWorkspaces", []));
  const [switchOpen, setSwitchOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const switchBtnRef = useRef(null);
  const cardRef = useRef(null);
  const menuRef = useRef(null);

  // The workspace menu sits beside the popup (clear of its edge) level with the current-workspace
  // card, like the sidebar's menu beside the sidebar. Without room there it drops down under the
  // card instead, at the card's width, flipping above when the space below runs out.
  const placeMenu = useCallback(() => {
    const card = cardRef.current?.getBoundingClientRect();
    const panel = switchBtnRef.current?.closest('[role="dialog"]')?.getBoundingClientRect();
    if (!card || !panel) return;
    const height = menuRef.current?.offsetHeight || Math.min(MENU_MAX_HEIGHT, workspaces.length * MENU_ROW_HEIGHT + 12);
    const clampTop = (top) => Math.max(8, Math.min(top, window.innerHeight - height - 8));
    if (panel.right + GAP + MENU_WIDTH <= window.innerWidth - 8) {
      // Level with the card; when it's too tall for that, its bottom lines up with the popup's.
      const top = card.top + height <= panel.bottom ? card.top : panel.bottom - height;
      setMenuPos({ left: panel.right + GAP, top: clampTop(top), width: MENU_WIDTH });
      return;
    }
    const below = card.bottom + 6;
    const top = below + height <= window.innerHeight - 8 ? below : card.top - height - 6;
    setMenuPos({ left: card.left, top: clampTop(top), width: card.width });
  }, [workspaces.length]);

  useEffect(() => {
    if (!switchOpen) return undefined;
    placeMenu();
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !switchBtnRef.current?.contains(e.target)) setSwitchOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setSwitchOpen(false);
    window.addEventListener("scroll", placeMenu, true);
    window.addEventListener("resize", placeMenu);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", placeMenu, true);
      window.removeEventListener("resize", placeMenu);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [switchOpen, placeMenu]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/v1/org/")
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setWorkspaces(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const current =
    workspaces.find((w) => String(w.id) === currentId) || readJSON("activeWorkspace", null) || { name: "this workspace" };
  const others = workspaces.filter((w) => String(w.id) !== currentId);
  const email = localStorage.getItem("email");
  const seatPrice = formatMoney(plan?.unit_amount || DEFAULT_SEAT_PRICE_CENTS, plan?.currency || "USD");
  const { title, badge } = copyFor(reason, plan);

  const logOut = () => {
    clearSession();
    navigate("/signin", { replace: true });
  };

  // Esc and clicking outside must not get past it, so the dialog's own close does nothing.
  return (
    <Dialog open onClose={() => {}} title={title} maxWidth={460} zClass="z-[100000]">
      <div className="flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-[#F6E9F4] text-[#A04A97] [@media(max-height:760px)]:h-10 [@media(max-height:760px)]:w-10">
          <Lock size={22} />
        </div>
        {badge && (
          <span className="rounded-full bg-[#F6E9F4] px-3 py-1 text-[12.5px] font-medium text-[#893F82]">{badge}</span>
        )}
      </div>

      <h2 className="mt-5 text-[24px] font-semibold tracking-[-0.02em] text-[#1A1A1A] [@media(max-height:760px)]:mt-3 [@media(max-height:760px)]:text-[21px]">{title}</h2>
      <p className="mt-1.5 text-[15px] text-[#52525B]">
        Pick a plan to keep working in <span className="font-semibold text-[#1A1A1A]">{current.name}</span>.
      </p>

      <div className="mt-5 flex items-start gap-3 rounded-[12px] border border-[#ECE9EE] bg-[#FAFAFB] px-4 py-3.5 [@media(max-height:760px)]:mt-3 [@media(max-height:760px)]:py-2.5">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-[#2F7D44]" />
        <p className="text-[14px] leading-[1.5] text-[#3F3F46]">
          <span className="font-semibold text-[#1A1A1A]">Nothing is deleted.</span> Your projects and documents stay
          exactly as you left them.
        </p>
      </div>

      <button
        type="button"
        onClick={() => navigate("/account/billing")}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#A04A97] py-3.5 [@media(max-height:760px)]:mt-4 [@media(max-height:760px)]:py-3 text-[15.5px] font-semibold text-white shadow-[0_6px_18px_rgba(160,74,151,0.35)] transition-colors hover:bg-[#8F4188]"
      >
        View plans <ArrowRight size={17} />
      </button>
      <p className="mt-3 text-center text-[13.5px] text-[#71717A]">
        Plans from <span className="font-semibold text-[#18101C]">{seatPrice}</span> per seat a month
        <span className="mx-1.5 text-[#C4C4C8]">·</span>Cancel anytime
      </p>

      {/* Footer spans the dialog's full width, so it cancels the panel's padding. */}
      <div className="-mx-7 -mb-7 mt-6 rounded-b-[18px] border-t border-[#ECE9EE] bg-[#FAFAFB] px-7 pb-6 pt-5 [@media(max-height:760px)]:mt-4 [@media(max-height:760px)]:pb-5 [@media(max-height:760px)]:pt-4">
        {others.length > 0 && (
          <>
            <p className="text-[14px] font-semibold text-[#1A1A1A]">Or continue in another workspace</p>
            <div ref={cardRef} className="mt-3 rounded-[12px] border border-[#ECE9EE] bg-white">
              <div className="flex items-center gap-3 px-3.5 py-3">
                <Avatar name={current.name} shape="square" size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-[#8A8A90]">Current workspace</p>
                  <p className="truncate text-[15px] font-semibold text-[#1A1A1A]">{current.name}</p>
                </div>
                <button
                  ref={switchBtnRef}
                  type="button"
                  onClick={() => setSwitchOpen((open) => !open)}
                  aria-expanded={switchOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-1 rounded-[8px] px-2 py-1 text-[14px] font-medium text-[#A04A97] hover:bg-[#FBF5FA]"
                >
                  Switch <ChevronsUpDown size={15} />
                </button>
              </div>
            </div>
          </>
        )}

        <div className={`flex items-center justify-between gap-3 ${others.length > 0 ? "mt-5" : ""}`}>
          <p className="truncate text-[13.5px] text-[#8A8A90]">{email ? `Signed in as ${email}` : ""}</p>
          <button
            type="button"
            onClick={logOut}
            className="flex shrink-0 items-center gap-1.5 rounded-[8px] px-2 py-1 text-[14px] font-medium text-[#3F3F46] hover:bg-[#F1F1F3]"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </div>

      {switchOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: menuPos.width }}
            className="z-[100001] font-outfit bg-white rounded-[12px] border border-[#ECE9EE] shadow-[0_12px_32px_rgba(26,13,27,0.16)] p-1.5 max-h-72 overflow-y-auto"
          >
            <WorkspaceMenuItems
              workspaces={workspaces}
              activeId={currentId}
              onSelect={(w) => {
                setSwitchOpen(false);
                if (String(w.id) !== currentId) switchWorkspace(w);
              }}
            />
          </div>,
          document.body
        )}
    </Dialog>
  );
};

export default SubscriptionPopup;
